import type { ReactNode } from 'react';

import type { JotBillCenterTab } from './JotBillCenterShell';

/**
 * Tab order is the biller's morning, in order (ADR-010): Home answers "what
 * needs me right now", and the worklists behind it answer "show me
 * everything". Home is FIRST and it is the bare-`/jotbill` default.
 *
 * `tasks` is deliberately absent. `BillingTasksTab` and its API slice stay in
 * the tree — the table behind them has no producer (nothing files a billing
 * task outside a manual admin POST), so the tab showed an empty list to every
 * organization. Removing the registration retires the surface; a follow-up
 * retires the code.
 */
export const JOTBILL_PRODUCTION_TAB_IDS = [
  'home',
  'claims',
  'encounters',
  'verifications',
  'patient-billing',
  'analytics',
] as const;

export type JotBillProductionTabId = (typeof JOTBILL_PRODUCTION_TAB_IDS)[number];

export type JotBillProductionTabContent = Record<
  Exclude<JotBillProductionTabId, 'analytics'>,
  ReactNode
> & { analytics?: ReactNode };

/**
 * Bare `/jotbill`, and the landing for any unrecognized `?tab=` — WHEN IT IS
 * VISIBLE. Home only works inside the claim workbench (its whole promise is
 * "Work this queue", which is the workbench's queue rail), so an organization
 * without that gate never sees the tab, and `JOTBILL_FALLBACK_TAB_ID` is
 * where bare `/jotbill` lands for them: the tab that was the default before
 * Home existed.
 */
export const JOTBILL_DEFAULT_TAB_ID: JotBillProductionTabId = 'home';

export const JOTBILL_FALLBACK_TAB_ID: JotBillProductionTabId = 'encounters';

type CreateJotBillProductionTabsOptions = {
  singularSubjectLabel: string;
  content: JotBillProductionTabContent;
  eligibilityBadgeCount?: number;
  eligibilityVisible: boolean;
  homeVisible: boolean;
  analyticsVisible?: boolean;
};

export const isJotBillProductionTabId = (tab: string | null): tab is JotBillProductionTabId =>
  Boolean(tab && JOTBILL_PRODUCTION_TAB_IDS.includes(tab as JotBillProductionTabId));

export const createJotBillProductionTabs = ({
  singularSubjectLabel,
  content,
  eligibilityBadgeCount = 0,
  eligibilityVisible,
  homeVisible,
  analyticsVisible = false,
}: CreateJotBillProductionTabsOptions): readonly JotBillCenterTab<JotBillProductionTabId>[] => [
  {
    id: 'home',
    label: 'Home',
    subtitle: 'What needs you right now',
    visible: homeVisible,
    content: content.home,
  },
  {
    id: 'claims',
    label: 'Claims',
    subtitle: 'Claim lifecycle',
    content: content.claims,
  },
  {
    id: 'encounters',
    label: 'Encounters',
    subtitle: 'Unified encounter claim list',
    content: content.encounters,
  },
  {
    id: 'verifications',
    label: 'Eligibility checks',
    subtitle: 'Verify before tomorrow’s visits',
    badgeCount: eligibilityBadgeCount,
    visible: eligibilityVisible,
    content: content.verifications,
  },
  {
    id: 'patient-billing',
    label: `${singularSubjectLabel} Billing`,
    subtitle: 'Invoice & collect',
    content: content['patient-billing'],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    subtitle: 'Claim outcomes and accounts receivable',
    visible: analyticsVisible,
    content: content.analytics,
  },
];
